import React from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { LANDING_PAGES } from "@/data/landing";
import { waLink } from "@/lib/whatsapp";
import Seo from "@/components/Seo";
import TrustBar from "@/components/TrustBar";
import VoltechLive from "@/components/VoltechLive";
import FaqSection from "@/components/FaqSection";
import { COMPANY } from "@/data/site";
import { MessageCircle, Phone, ArrowUpRight, Check } from "lucide-react";

function DataTable({ config, testId }) {
  return (
    <div className="industrial-card overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            {config.cols.map((c, i) => (
              <th
                key={i}
                className={`text-left font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 px-4 py-3 ${
                  i === 0 ? "" : ""
                }`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {config.rows.map((row, ri) => (
            <tr key={ri} className="border-b border-white/5 last:border-0">
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={`px-4 py-3 ${
                    ci === 0
                      ? "font-display uppercase text-white text-[13px] tracking-tight"
                      : "text-zinc-300"
                  } ${ci === row.length - 1 && config.cols[ci] === "Voltech LFP" ? "text-[#e63946] font-semibold" : ""}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function LandingPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").replace(/\/$/, "");
  const cfg = LANDING_PAGES[slug];
  if (!cfg) return <Navigate to="/products" replace />;

  const wa = waLink(cfg.waMessage);

  return (
    <div data-testid={`landing-${slug}`}>
      <Seo path={`/${slug}`} title={cfg.seoTitle} description={cfg.seoDescription} image={cfg.heroImage} />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-noise opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow">{cfg.eyebrow}</div>
            <h1 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] tracking-tighter mt-5">
              {cfg.heroHeadline}
            </h1>
            <p className="mt-6 text-zinc-300 text-base md:text-lg max-w-xl leading-relaxed">
              {cfg.heroSub}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={wa} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="landing-hero-wa">
                <MessageCircle size={16} /> {cfg.ctaText}
              </a>
              <a href={`tel:${COMPANY.phoneSales}`} className="btn-secondary" data-testid="landing-hero-call">
                <Phone size={16} /> Call {COMPANY.phoneSales}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="industrial-card overflow-hidden">
              <img src={cfg.heroImage} alt={cfg.eyebrow} className="w-full h-[300px] md:h-[380px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* Comparison (LFP) */}
      {cfg.comparison && (
        <section className="py-16 md:py-24 border-b border-white/10" data-testid="landing-comparison">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-2xl md:text-4xl uppercase tracking-tighter mb-8">
              {cfg.comparison.title}
            </h2>
            <DataTable config={cfg.comparison} testId="comparison-table" />
          </div>
        </section>
      )}

      {/* Transparent pricing (Custom) */}
      {cfg.pricing && (
        <section className="py-16 md:py-24 border-b border-white/10" data-testid="landing-pricing">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-2">
              <span className="tag-chip tag-chip-red">Transparent pricing</span>
            </div>
            <h2 className="font-display text-2xl md:text-4xl uppercase tracking-tighter mb-8">
              {cfg.pricing.title}
            </h2>
            <DataTable config={cfg.pricing} testId="pricing-table" />
            <p className="text-xs text-zinc-500 mt-4 font-mono">{cfg.pricing.note}</p>
          </div>
        </section>
      )}

      {/* Product range */}
      {cfg.range && (
        <section className="py-16 md:py-24 border-b border-white/10" data-testid="landing-range">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-2xl md:text-4xl uppercase tracking-tighter mb-8">
              {cfg.range.title}
            </h2>
            <DataTable config={cfg.range} testId="range-table" />
            {cfg.range.note && (
              <p className="text-xs text-zinc-500 mt-4 font-mono">{cfg.range.note}</p>
            )}
          </div>
        </section>
      )}

      {/* Capabilities (Custom) */}
      {cfg.capabilities && (
        <section className="py-16 md:py-24 border-b border-white/10" data-testid="landing-capabilities">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-2xl md:text-4xl uppercase tracking-tighter mb-8">
              {cfg.capabilities.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cfg.capabilities.items.map(([label, val], i) => (
                <div key={i} className="industrial-card p-5" data-testid={`capability-item-${i}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#e63946]">
                    {label}
                  </div>
                  <div className="text-sm text-zinc-200 mt-2">{val}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Process (Custom) */}
      {cfg.process && (
        <section className="py-16 md:py-24 border-b border-white/10 bg-[#0a0a0c]" data-testid="landing-process">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-2xl md:text-4xl uppercase tracking-tighter mb-8">
              {cfg.process.title}
            </h2>
            <div className="space-y-3">
              {cfg.process.steps.map((s, i) => (
                <div key={i} className="industrial-card p-5 flex items-center gap-4" data-testid={`process-step-${i}`}>
                  <div className="font-display text-xl text-[#e63946] w-8 flex-shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="text-sm text-zinc-200">{s}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bundle + WhoBuys (Solar) */}
      {(cfg.bundle || cfg.whoBuys) && (
        <section className="py-16 md:py-24 border-b border-white/10" data-testid="landing-extra">
          <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {cfg.bundle && (
              <div className="industrial-card p-7" data-testid="landing-bundle">
                <div className="eyebrow">Turnkey</div>
                <h3 className="font-display text-xl md:text-2xl uppercase mt-3 leading-tight">
                  {cfg.bundle.title}
                </h3>
                <p className="text-sm text-zinc-400 mt-4 leading-relaxed">{cfg.bundle.body}</p>
              </div>
            )}
            {cfg.whoBuys && (
              <div className="industrial-card p-7" data-testid="landing-whobuys">
                <div className="eyebrow">Trusted by</div>
                <h3 className="font-display text-xl md:text-2xl uppercase mt-3 leading-tight">
                  {cfg.whoBuys.title}
                </h3>
                <ul className="mt-4 space-y-2">
                  {cfg.whoBuys.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check size={14} className="text-[#e63946] mt-1 flex-shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      <VoltechLive />

      <FaqSection faqs={cfg.faqs} testId="landing-faq" />

      {/* Final CTA */}
      <section className="py-20 md:py-28" data-testid="landing-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="industrial-card p-10 md:p-16 text-center">
            <div className="eyebrow">Get started</div>
            <h2 className="font-display text-3xl md:text-5xl uppercase mt-4 tracking-tighter leading-[0.95]">
              {cfg.ctaText}
            </h2>
            <p className="text-zinc-400 mt-4 max-w-xl mx-auto text-sm md:text-base">
              Chat directly with our engineers on WhatsApp — real answers, fast quotes, no chatbot.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={wa} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="landing-cta-wa">
                <MessageCircle size={16} /> {cfg.ctaText}
              </a>
              <Link to="/contact" className="btn-secondary" data-testid="landing-cta-form">
                Use enquiry form <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
